import * as React from 'react';
import { ArrowLeft, Check, Info, Shield, X, Zap } from 'lucide-react';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import {
  FilterChip,
  ListGroupLabel,
  RecordRow,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceHeader,
  WorkspaceInput,
  linkishClass,
  useToast,
} from '@/components/ui/workspace';
import { ApiError } from '@/lib/api/client';
import { useDiscoverySummaryQuery } from '@/lib/api/discovery';
import {
  SUGGESTION_DISMISS_REASONS,
  useAddSuggestions,
  useDismissSuggestion,
  usePatchSuggestion,
  useRestoreSuggestion,
  useRunSuggestions,
  useSuggestionsQuery,
  type Suggestion,
  type SuggestionDismissReason,
} from '@/lib/api/discovery-suggestions';
import { newIdempotencyKey } from '@/lib/api/engagement-requirements';
import { useDiscoveryNavigate } from '@/components/discovery/discovery-layout';
import { formatDay, plural } from '@/components/discovery/discovery-logic';
import { CriticalityChip } from '../criticality';
import { asCriticality } from '../requirements-logic';
import { SuggestionCard } from './suggestion-card';
import {
  DISMISS_LABEL,
  addLine,
  addedToast,
  awaitingText,
  dismissedText,
  lastRunText,
  pickedSummary,
  signoffWords,
} from './suggestions-logic';

/**
 * Selection -> Requirements -> Add from discovery (T-0190, E12-F10): the
 * fourth way to add requirements. AI suggests them from confirmed
 * evidence only, in two groups (in the firm library, not in it), each
 * quoting what people said. Confident ones arrive selected; nothing is
 * added until the consultant confirms. Prototype: `requirementsView` and
 * `selPage` in `docs/discovery/prototype/discovery.js` (`#requirements`).
 */

const messageOf = (e: unknown, fallback: string) => (e instanceof ApiError ? e.message : fallback);

export function DiscoverySuggestionsScreen({
  engagementId,
  onOpenRequirements,
}: {
  engagementId: string;
  /** Back to the requirements list, optionally searching for numbers just added. */
  onOpenRequirements: (q?: string) => void;
}) {
  const q = useSuggestionsQuery(engagementId);
  const summary = useDiscoverySummaryQuery(engagementId);
  const patch = usePatchSuggestion(engagementId);
  const run = useRunSuggestions(engagementId);
  const dismiss = useDismissSuggestion(engagementId);
  const restore = useRestoreSuggestion(engagementId);
  const go = useDiscoveryNavigate(engagementId);
  const { toast } = useToast();
  const [adding, setAdding] = React.useState(false);
  const [dismissing, setDismissing] = React.useState<Suggestion | null>(null);

  const signoff = signoffWords(summary.data);
  const header = (
    <WorkspaceHeader
      title="Add from discovery"
      meta="Selection · Requirements · one of four ways to add requirements: from the library, a spreadsheet, by hand, or from discovery."
      actions={
        <>
          <WorkspaceButton variant="tertiary" size="md" onClick={() => go({ to: 'overview' })}>
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Discovery overview
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            title="Requirements sign-off by the client"
            onClick={() => go({ to: 'signoff' })}
          >
            <Shield className="h-3.5 w-3.5" aria-hidden="true" /> Sign-off
            {signoff ? ` · ${signoff}` : ''}
          </WorkspaceButton>
          <WorkspaceButton size="md" onClick={() => onOpenRequirements()}>
            Open requirements
          </WorkspaceButton>
        </>
      }
    />
  );

  if (q.error) {
    const off = q.error instanceof ApiError && q.error.code === 'not_found';
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 border-t border-ink-100 p-8 text-center">
          {off ? (
            <>
              <h2 className="text-h2 font-semibold text-ink-900">Discovery is not on here</h2>
              <p className="max-w-md text-body-sm text-ink-500">
                Suggestions come from the Discovery module, which is not turned on for this
                engagement. Add requirements from the library, a spreadsheet or by hand.
              </p>
              <WorkspaceButton onClick={() => onOpenRequirements()}>
                Open requirements
              </WorkspaceButton>
            </>
          ) : (
            <>
              <ErrorMessage error={q.error} />
              <WorkspaceButton onClick={() => void q.refetch()}>Try again</WorkspaceButton>
            </>
          )}
        </div>
      </div>
    );
  }
  if (!q.data) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        {header}
        <div className="flex flex-1 items-center justify-center gap-2 border-t border-ink-100 text-body-sm text-ink-500">
          <Spinner /> Loading the suggestions…
        </div>
      </div>
    );
  }

  const d = q.data;
  const g = d.groups;
  const open = [...g.library, ...g.new];
  const { picked, toAdd, toLink } = pickedSummary(open);
  const done = [...g.added, ...g.linked].sort((a, b) =>
    (b.decided_at ?? '').localeCompare(a.decided_at ?? ''),
  );
  const awaiting = awaitingText(d.awaiting_review_not_used);
  const fresh = d.evidence_since_last_run;
  const running = Boolean(d.active_run) || run.isPending;

  const runAgain = () =>
    run.mutate(undefined, {
      onSuccess: () =>
        toast({
          message: `Running again: AI reads only the ${plural(fresh, 'item')} confirmed since the last run. New suggestions appear here.`,
        }),
      onError: (e) => toast({ message: messageOf(e, 'Could not run again.'), tone: 'critical' }),
    });

  const card = (s: Suggestion) => (
    <SuggestionCard
      key={s.id}
      s={s}
      busy={patch.isPending}
      onPick={(next) => patch.mutate({ id: s.id, patch: { picked: next } })}
      onCriticality={(c) => patch.mutate({ id: s.id, patch: { criticality: c } })}
      onSwitch={(libraryItemId) => {
        const alt = s.alternatives.find((a) => a.id === libraryItemId);
        patch.mutate(
          { id: s.id, patch: { library_item_id: libraryItemId } },
          {
            onSuccess: () =>
              toast({
                message: `Switched to ${alt?.number ?? 'the other match'}. The evidence stays the same, and the switch is recorded.`,
              }),
            onError: (e) =>
              toast({ message: messageOf(e, 'Could not switch it.'), tone: 'critical' }),
          },
        );
      }}
      onDismiss={() => setDismissing(s)}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      {header}
      <div className="flex min-h-0 flex-1 flex-col border-t border-ink-100">
        <div className="flex flex-none items-start gap-4 border-b border-ink-100 bg-paper-0 px-6 py-3.5">
          <div className="min-w-0 flex-1">
            <h2 className="text-h3 font-semibold text-ink-900">Suggested from discovery</h2>
            <p className="text-meta text-ink-500">{lastRunText(d)}</p>
            {awaiting ? (
              <p className="mt-1 flex items-center gap-1 text-meta text-ink-500">
                <Info className="h-3 w-3 flex-none" aria-hidden="true" /> {awaiting}
              </p>
            ) : null}
          </div>
          <WorkspaceButton
            disabled={fresh === 0 || running}
            title={fresh === 0 ? 'Nothing new to read since the last run' : undefined}
            onClick={runAgain}
          >
            {running ? <Spinner /> : <Zap className="h-3.5 w-3.5" aria-hidden="true" />}
            {running
              ? 'Running…'
              : fresh > 0
                ? `Run again (${plural(fresh, 'new item')})`
                : 'Run again'}
          </WorkspaceButton>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 pb-6 pt-2">
          {g.library.length ? (
            <section aria-label="In your requirement library" className="mb-6">
              <ListGroupLabel count={g.library.length}>In your requirement library</ListGroupLabel>
              <div className="flex flex-col gap-3">{g.library.map(card)}</div>
            </section>
          ) : null}
          {g.new.length ? (
            <section aria-label="Not in the library" className="mb-6">
              <ListGroupLabel count={g.new.length}>Not in the library</ListGroupLabel>
              <p className="-mt-1 mb-2 px-1.5 text-meta text-ink-500">
                Client-specific. They get a client number and stay out of the firm library unless a
                curator adds them.
              </p>
              <div className="flex flex-col gap-3">{g.new.map(card)}</div>
            </section>
          ) : null}
          {open.length === 0 ? (
            <div className="px-6 py-14 text-center text-body-sm text-ink-500">
              <strong className="mb-1 block text-h3 font-semibold text-ink-900">
                {d.last_run ? 'Every suggestion is decided' : 'No suggestions yet'}
              </strong>
              {d.last_run
                ? 'Run again after more evidence is confirmed.'
                : 'They are built once evidence is confirmed in Discovery.'}
            </div>
          ) : null}
          {done.length ? (
            <section aria-label="Added" className="mb-6">
              <ListGroupLabel count={done.length}>Added</ListGroupLabel>
              <div role="list">
                {done.map((s) => (
                  <DoneRow
                    key={s.id}
                    icon={<Check className="h-3.5 w-3.5" />}
                    number={
                      s.result_requirement?.number ??
                      s.match?.number ??
                      s.library_item?.number ??
                      ''
                    }
                    text={s.text}
                    meta={`${s.status === 'linked' ? 'evidence linked' : 'added'}${s.decided_at ? ` ${formatDay(s.decided_at)}` : ''}`}
                    action={
                      <button
                        type="button"
                        className={linkishClass()}
                        onClick={() =>
                          onOpenRequirements(s.result_requirement?.number ?? s.match?.number)
                        }
                      >
                        Open in Selection
                      </button>
                    }
                  />
                ))}
              </div>
            </section>
          ) : null}
          {g.dismissed.length ? (
            <section aria-label="Dismissed" className="mb-6">
              <ListGroupLabel count={g.dismissed.length}>Dismissed</ListGroupLabel>
              <div role="list">
                {g.dismissed.map((s) => (
                  <DoneRow
                    key={s.id}
                    muted
                    icon={<X className="h-3.5 w-3.5" />}
                    number={s.library_item?.number ?? 'New'}
                    text={s.text}
                    meta={dismissedText(s)}
                    action={
                      <button
                        type="button"
                        className={linkishClass()}
                        disabled={restore.isPending}
                        onClick={() =>
                          restore.mutate(s.id, {
                            onSuccess: () => toast({ message: 'Restored. It is open again.' }),
                            onError: (e) =>
                              toast({
                                message: messageOf(e, 'Could not restore it.'),
                                tone: 'critical',
                              }),
                          })
                        }
                      >
                        Restore
                      </button>
                    }
                  />
                ))}
              </div>
            </section>
          ) : null}
          {g.withdrawn.length ? (
            <section aria-label="Withdrawn" className="mb-6">
              <ListGroupLabel count={g.withdrawn.length}>Withdrawn</ListGroupLabel>
              <div role="list">
                {g.withdrawn.map((s) => (
                  <DoneRow
                    key={s.id}
                    muted
                    icon={<X className="h-3.5 w-3.5" />}
                    number={s.library_item?.number ?? 'New'}
                    text={s.text}
                    meta="The evidence it rested on was withdrawn"
                  />
                ))}
              </div>
            </section>
          ) : null}
        </div>

        {open.length ? (
          <div className="flex flex-none items-center gap-3 border-t border-ink-100 bg-paper-50 px-5 py-3.5">
            <p
              role="status"
              aria-label="What will be added"
              className="flex flex-1 flex-wrap gap-4 text-body-sm text-ink-700"
            >
              <span>
                <b className="font-semibold tabular-nums text-ink-900">{picked.length}</b> selected
              </span>
              <span>
                <b className="font-semibold tabular-nums text-ink-900">{toAdd}</b> to add
              </span>
              {toLink ? (
                <span>
                  <b className="font-semibold tabular-nums text-ink-900">{toLink}</b>{' '}
                  {toLink === 1 ? 'evidence link' : 'evidence links'}
                </span>
              ) : null}
              <span>
                Confident suggestions arrive selected. Nothing is added until you confirm.
              </span>
            </p>
            <WorkspaceButton
              variant="primary"
              size="md"
              disabled={picked.length === 0}
              onClick={() => setAdding(true)}
            >
              Add to requirements
            </WorkspaceButton>
          </div>
        ) : null}
      </div>

      {adding ? (
        <AddDialog
          engagementId={engagementId}
          picked={picked}
          onClose={() => setAdding(false)}
          onAdded={(message) =>
            toast({ message, actions: [{ label: 'Open', onClick: () => onOpenRequirements() }] })
          }
        />
      ) : null}
      {dismissing ? (
        <DismissDialog
          s={dismissing}
          busy={dismiss.isPending}
          onClose={() => setDismissing(null)}
          onDismiss={(reason, text) =>
            dismiss.mutate(
              { id: dismissing.id, reason, ...(text ? { text } : {}) },
              {
                onSuccess: () => {
                  setDismissing(null);
                  toast({ message: 'Dismissed. The reason is kept, and it can be restored.' });
                },
                onError: (e) =>
                  toast({ message: messageOf(e, 'Could not dismiss it.'), tone: 'critical' }),
              },
            )
          }
        />
      ) : null}
    </div>
  );
}

/** A decided suggestion, one line (`.done-row`). */
function DoneRow({
  icon,
  number,
  text,
  meta,
  action,
  muted = false,
}: {
  icon: React.ReactNode;
  number: string;
  text: string;
  meta: string;
  action?: React.ReactNode;
  muted?: boolean;
}) {
  return (
    <div role="listitem">
      <RecordRow icon={icon} muted={muted}>
        <span className="flex-none font-mono text-meta font-medium text-ink-700">{number}</span>
        <span className="min-w-0 flex-1 truncate">{text}</span>
        <span className="flex-none text-meta text-ink-500">{meta}</span>
        {action}
      </RecordRow>
    </div>
  );
}

function AddDialog({
  engagementId,
  picked,
  onClose,
  onAdded,
}: {
  engagementId: string;
  picked: ReadonlyArray<Suggestion>;
  onClose: () => void;
  onAdded: (message: string) => void;
}) {
  const add = useAddSuggestions(engagementId);
  // One key per confirmed action: a retry of this same add is applied once (rule 23).
  const [key] = React.useState(newIdempotencyKey);
  const [error, setError] = React.useState<string | null>(null);
  const adds = picked.filter((s) => !s.match).length;

  return (
    <WorkspaceDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title={
        adds > 0
          ? `Add ${plural(adds, 'requirement')} from discovery`
          : `Link evidence to ${plural(picked.length, 'requirement')}`
      }
      description="Each one carries a discovery citation: the evidence items, with who said them, when, and in which interview or document."
      actions={
        <>
          <WorkspaceButton variant="tertiary" size="md" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={add.isPending}
            onClick={() => {
              setError(null);
              add.mutate(
                { ids: picked.map((s) => s.id), key },
                {
                  onSuccess: (r) => {
                    onClose();
                    onAdded(addedToast(r));
                  },
                  onError: (e) => setError(messageOf(e, 'Could not add them.')),
                },
              );
            }}
          >
            {add.isPending ? 'Adding…' : 'Add to requirements'}
          </WorkspaceButton>
        </>
      }
    >
      <ul className="max-h-[200px] overflow-y-auto rounded-card border border-ink-100">
        {picked.map((s) => {
          const line = addLine(s);
          return (
            <li
              key={s.id}
              className="grid grid-cols-[96px_64px_1fr_auto] items-start gap-2.5 border-b border-ink-100 px-3 py-2 text-meta last:border-b-0"
            >
              <span className="text-ink-500">{line.verb}</span>
              <span className="font-mono text-ink-900">{line.number}</span>
              <span className="text-ink-700">{s.text}</span>
              {/* What it arrives with; a link leaves the requirement's own criticality alone. */}
              {s.match ? (
                <span className="whitespace-nowrap text-ink-500">Keeps its criticality</span>
              ) : (
                <CriticalityChip value={asCriticality(s.criticality)} />
              )}
            </li>
          );
        })}
      </ul>
      <p className="text-meta text-ink-500">
        Library matches keep their library number. New ones get the next client number in their
        module. Numbers are assigned by Caliber and never reused.
      </p>
      {error ? (
        <p role="alert" className="text-meta text-posture-critical">
          {error}
        </p>
      ) : null}
    </WorkspaceDialog>
  );
}

function DismissDialog({
  s,
  busy,
  onClose,
  onDismiss,
}: {
  s: Suggestion;
  busy: boolean;
  onClose: () => void;
  onDismiss: (reason: SuggestionDismissReason, text?: string) => void;
}) {
  const [reason, setReason] = React.useState<SuggestionDismissReason | null>(null);
  const [text, setText] = React.useState('');
  const ok = reason !== null && (reason !== 'other' || text.trim().length > 0);
  return (
    <WorkspaceDialog
      open
      onOpenChange={(next) => !next && onClose()}
      title="Dismiss this suggestion"
      description="The reason is kept with it, so a later run does not suggest it again for the same evidence."
      actions={
        <>
          <WorkspaceButton variant="tertiary" size="md" onClick={onClose}>
            Cancel
          </WorkspaceButton>
          <WorkspaceButton
            variant="primary"
            size="md"
            disabled={!ok || busy}
            onClick={() =>
              reason && onDismiss(reason, reason === 'other' ? text.trim() : undefined)
            }
          >
            Dismiss
          </WorkspaceButton>
        </>
      }
    >
      <p className="rounded-card border border-ink-100 bg-paper-50 px-3 py-2.5 text-body-sm text-ink-700">
        {s.text}
      </p>
      <div>
        <p className="mb-1.5 text-meta font-medium text-ink-700">Reason</p>
        <div role="group" aria-label="Reason" className="flex flex-wrap gap-1.5">
          {SUGGESTION_DISMISS_REASONS.map((r) => (
            <FilterChip key={r} on={reason === r} onClick={() => setReason(r)}>
              {DISMISS_LABEL[r]}
            </FilterChip>
          ))}
        </div>
        {reason === 'other' ? (
          <WorkspaceInput
            autoFocus
            aria-label="Reason in a few words"
            placeholder="A few words"
            className="mt-2"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        ) : null}
      </div>
    </WorkspaceDialog>
  );
}
